import { updateElements } from "./updateElements";

type QfcAvailabilityResponse = {
  isVariantAvailable: boolean;
};

const getSoldOutLabel = () => {
  const body = document.body;
  if (!body) {
    return "";
  }

  const { labelSoldOut } = body.dataset;

  return labelSoldOut ?? "";
};

const disableQfcUnavailableButton = (button: HTMLButtonElement) => {
  if (button.dataset.qfcAvailability === "unavailable") {
    return;
  }

  button.disabled = true;
  button.dataset.qfcAvailability = "unavailable";
  button.setAttribute("aria-disabled", "true");

  const soldOutLabel = getSoldOutLabel();

  if (soldOutLabel.length > 0) {
    const applyLabel = () => {
      if (button.textContent !== soldOutLabel) {
        button.textContent = soldOutLabel;
      }

      button.setAttribute("aria-label", soldOutLabel);
    };

    applyLabel();

    const labelObserver = new MutationObserver(() => {
      if (button.dataset.qfcAvailability !== "unavailable") {
        labelObserver.disconnect();
        return;
      }

      applyLabel();
    });

    labelObserver.observe(button, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }
};

const getQfcAvailabilityUrl = (button: HTMLButtonElement) => {
  const wishlistItem = button.closest(".swym-wishlist-item");
  const detailContainer = button.closest(
    ".swym-wishlist-product-detail-text-container",
  );

  const root = wishlistItem ?? detailContainer;

  if (!root) {
    return "";
  }

  const link =
    root.querySelector<HTMLAnchorElement>(".swym-wishlist-image-wrapper") ??
    root.querySelector<HTMLAnchorElement>(".swym-more-details-btn");

  if (!link) {
    return "";
  }

  const href = link.getAttribute("href");

  if (!href) {
    return "";
  }

  let productUrl: URL;

  try {
    productUrl = href.startsWith("http")
      ? new URL(href)
      : new URL(href, window.location.origin);
  } catch (_error) {
    return "";
  }

  const variantId = productUrl.searchParams.get("variant");

  if (!variantId) {
    return "";
  }

  const availabilityUrl = new URL(
    productUrl.pathname,
    productUrl.origin || window.location.origin,
  );

  availabilityUrl.searchParams.set("variant", variantId);
  availabilityUrl.searchParams.set("view", "qfc-availability");

  return availabilityUrl.toString();
};

const initializedButtons = new WeakSet<HTMLButtonElement>();

const updateWishlistButtonAvailability = async (
  button: HTMLButtonElement,
) => {
  if (button.dataset.qfcAvailability === "unavailable") {
    return;
  }

  const availabilityUrl = getQfcAvailabilityUrl(button);

  if (!availabilityUrl) {
    return;
  }

  try {
    const response = await fetch(availabilityUrl);

    if (!response.ok) {
      return;
    }

    const availability: QfcAvailabilityResponse = await response.json();

    if (!availability.isVariantAvailable) {
      disableQfcUnavailableButton(button);
    }
  } catch (_error) {
    // eslint-disable-next-line no-console
    console.error("QFC availability check failed");
  }
};

type SchoolQuoteConfig = {
  url: string;
  label: string;
  tag: string;
};

type ProductJson = {
  tags?: string[];
};

declare global {
  interface Window {
    carbatecSchoolQuote?: SchoolQuoteConfig;
  }
}

const SCHOOL_TAG_FALLBACK = "school";
const WISHLIST_ADD_TO_CART_BUTTON_SELECTOR =
  ".swym-add-to-cart-btn, .swym-wishlist-add-to-cart-btn";
const WISHLIST_ITEM_SELECTOR = ".swym-wishlist-item";
const WISHLIST_IMAGE_LINK_SELECTOR = "a.swym-wishlist-image-wrapper";
const WISHLIST_DETAIL_SELECTOR = ".swym-wishlist-product-detail-content";
const WISHLIST_DETAIL_LINK_SELECTOR = ".swym-more-details-btn.swym-is-anchor";

const getQuoteConfig = (): SchoolQuoteConfig | null => {
  const config = window.carbatecSchoolQuote;
  if (!config || !config.url || !config.label) return null;
  return {
    url: config.url,
    label: config.label,
    tag: config.tag || SCHOOL_TAG_FALLBACK,
  };
};

const normalizeTag = (value: string): string => value.trim().toLowerCase();

const hasSchoolTag = (tags: string[], schoolTag: string): boolean => {
  const expected = normalizeTag(schoolTag || SCHOOL_TAG_FALLBACK);
  return tags.some((tag) => tag && normalizeTag(tag) === expected);
};

const extractHandleFromHref = (href: string): string | null => {
  try {
    const url = new URL(href, window.location.origin);
    const segments = url.pathname.split("/");
    const productsIndex = segments.indexOf("products");
    if (productsIndex === -1) return null;
    const handle = segments[productsIndex + 1] || "";
    return handle || null;
  } catch {
    return null;
  }
};

const extractHandleFromContext = (context: HTMLElement): string | null => {
  const imageLink = context.querySelector(WISHLIST_IMAGE_LINK_SELECTOR);
  const detailsLink = context.querySelector(WISHLIST_DETAIL_LINK_SELECTOR);
  const link =
    imageLink instanceof HTMLAnchorElement
      ? imageLink
      : detailsLink instanceof HTMLAnchorElement
        ? detailsLink
        : null;
  if (!link) return null;
  return extractHandleFromHref(link.href);
};

const attachSchoolLabelObserver = (
  button: HTMLElement,
  label: string,
): void => {
  if (button.dataset.schoolLabelBound === "true") return;
  button.dataset.schoolLabelBound = "true";
  button.dataset.schoolLabel = label;

  const observer = new MutationObserver(() => {
    const expectedLabel = button.dataset.schoolLabel;
    if (!expectedLabel) return;
    if (button.textContent === expectedLabel) return;
    button.textContent = expectedLabel;
  });

  observer.observe(button, {
    childList: true,
    characterData: true,
    subtree: true,
  });
};

const markButtonIfSchool = (
  button: HTMLElement,
  config: SchoolQuoteConfig,
): void => {
  const gridItem = button.closest(WISHLIST_ITEM_SELECTOR);
  const detailItem = button.closest(WISHLIST_DETAIL_SELECTOR);

  const contextElement = gridItem ?? detailItem;
  if (!(contextElement instanceof HTMLElement)) return;
  if (contextElement.dataset.schoolChecked === "true") return;

  const handle = extractHandleFromContext(contextElement);

  if (!handle) {
    contextElement.dataset.schoolChecked = "true";
    return;
  }

  void fetch(`/products/${encodeURIComponent(handle)}.js`)
    .then((response) => (response.ok ? response.json() : null))
    .then((data: ProductJson | null) => {
      contextElement.dataset.schoolChecked = "true";
      if (!data || !Array.isArray(data.tags)) return;
      if (!hasSchoolTag(data.tags, config.tag)) return;

      button.dataset.schoolProduct = "true";
      button.textContent = config.label;
      button.setAttribute("aria-label", config.label);
      attachSchoolLabelObserver(button, config.label);
    })
    .catch(() => {
      contextElement.dataset.schoolChecked = "true";
    });
};

const handleButtonClick = (button: HTMLElement) => {
  if (button.dataset.schoolProduct === "true") return;

  const originalText = button.textContent;

  const buttonObserver = new MutationObserver(() => {
    if (button.textContent !== originalText) {
      buttonObserver.disconnect();
      const cartDrawer = document.querySelector("cart-drawer");
      if ((cartDrawer as any)?.renderContents) {
        void updateElements([".drawer__inner", ".cart-count-bubble"]);
        (cartDrawer as any).renderContents();
        cartDrawer.classList.remove("is-empty");
      }
    }
  });

  buttonObserver.observe(button, { attributes: true });
};

const findAndHandleButtons = (): void => {
  const buttons: NodeListOf<HTMLElement> = document.querySelectorAll(
    WISHLIST_ADD_TO_CART_BUTTON_SELECTOR,
  );
  if (!buttons.length) return;

  const quoteConfig = getQuoteConfig();

  buttons.forEach((button) => {
    if (button.dataset.wishlistBound !== "true") {
      button.dataset.wishlistBound = "true";
      button.addEventListener("click", () => handleButtonClick(button));
    }
    if (quoteConfig) {
      markButtonIfSchool(button, quoteConfig);
    }
  });
};

const handleWishlistAddToCart = () => {
  const applyToButtons = () => {
    const buttons = document.querySelectorAll<HTMLButtonElement>(
      ".swym-add-to-cart-btn, .swym-wishlist-add-to-cart-btn",
    );

    buttons.forEach((button) => {
      if (initializedButtons.has(button)) {
        return;
      }

      initializedButtons.add(button);

      button.addEventListener("click", () => handleButtonClick(button));
      void updateWishlistButtonAvailability(button);
    });
  };

  const mainObserver = new MutationObserver(() => {
    applyToButtons();
    findAndHandleButtons();
  });

  mainObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  applyToButtons();
  findAndHandleButtons();
};

const handleWishlistQuoteClick = (event: MouseEvent): void => {
  const quoteConfig = getQuoteConfig();
  if (!quoteConfig) return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const button = target.closest(WISHLIST_ADD_TO_CART_BUTTON_SELECTOR);
  if (!(button instanceof HTMLButtonElement)) return;
  if (button.dataset.schoolProduct !== "true") return;

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  window.location.href = quoteConfig.url;
};

const initWishlistHandlers = () => {
  handleWishlistAddToCart();
  document.addEventListener("click", handleWishlistQuoteClick, true);
};

initWishlistHandlers();
